//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

import moment from 'moment';
import PQueue from 'p-queue';
import { mapGetters } from 'vuex';
import { withOpacity } from '@/helpers/colour';
import { PALETTE_COLOURS } from '@/store/types/getters';
import { COLOURS } from '@/store/types/namespaces';
import { importPlotly } from './Plotly';
import BaseChart from './baseChart';

let d3Ref;

function safeCompare(a, b) {
  // Null checking
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }

  const leftNumber = Number.parseFloat(a);
  const rightNumber = Number.parseFloat(b);
  if (Number.isNaN(leftNumber) || Number.isNaN(rightNumber)) {
    return (a.toString()).localeCompare(b.toString());
  }
  return leftNumber - rightNumber;
}
function getYMin(data, forceZero = false) {
  const ys = [];
  data.forEach((d) => {
    if (d.visible === false) return;
    d.values.forEach((v) => {
      ys.push(v.y);
    });
  });
  ys.sort(safeCompare);
  const yMin = ys[0] || 0;
  return forceZero ? Math.min(yMin, 0) : yMin;
}

function getYMax(data) {
  const ys = [];
  data.forEach((d) => {
    if (d.visible === false) return;
    d.values.forEach((v) => {
      ys.push(v.y);
    });
  });
  ys.sort(safeCompare).reverse();
  return ys[0] || 1;
}

const queue = new PQueue({ concurrency: 1 });
async function throttleDraw(Plotly, element, traces, layout) {
  await queue.add(() => new Promise((resolve) => {
    setTimeout(() => {
      if (!element) return resolve();

      return Plotly.newPlot(element, traces, layout, { showLink: false, responsive: true })
        .then(() => {
          resolve();
        });
    }, 50);
  }));
}

export default {
  name: 'DpChart',
  mixins: [BaseChart],
  props: {
    data: { type: Array, default: () => [] },
    binSize: { type: String, default: null },
    noGaps: { type: Boolean, default: false },
    xaxis: { type: Object, default: null },
    yaxis: { type: Object, default: null },
    barmode: { type: String, default: null },
    allowLegendEvents: { type: Boolean, default: false },
    legendOnTop: { type: Boolean, default: false },
    alwaysShowLegend: { type: Boolean, default: false },
    chartParams: { type: Object, required: true },
  },
  data() {
    return {
      legendElements: [],
      showNoDataMessage: false,
      beforePrintEvent: this.beforePrintHandle.bind(this),
      afterPrintEvent: this.afterPrintHandle.bind(this),
      test: null,
      prevWidth: null,
    };
  },
  computed: mapGetters(COLOURS, [PALETTE_COLOURS]),
  watch: {
    data() {
      this.runWithProps();
    },
    noGaps() {
      this.runWithProps();
    },
    chartParams: {
      handler() {
        this.run();
      },
      deep: true,
    },
  },
  mounted() {
    this.runWithProps();
  },
  beforeDestroy() {
    this.$refs.element.removeEventListener('plotly_click', this.handleClick);
    this.clearLegendEvents();

    window.removeEventListener('beforeprint', this.beforePrintEvent);
    window.removeEventListener('afterprint', this.afterPrintEvent);
  },
  methods: {
    runWithProps() {
      this.run({
        data: this.data,
        noGaps: this.noGaps,
        isPercent: this.isPercent,
        barmode: this.barmode,
        xaxis: this.xaxis,
        yaxis: this.yaxis,
        binSize: this.binSize,
      });
    },
    async getLayout(displayData, traces = []) {
      const {
        noGaps,
        binSize,
        isPercent,
        data,
      } = displayData;

      const yMin = getYMin(data, isPercent);

      const c = this[PALETTE_COLOURS];
      const layout = {
        xaxis: {
          type: 'category',
          range: [],
          automargin: true,
          gridcolor: c.neutral[0],
          titlefont: {
            family: 'Proxima Nova, Arial',
            size: 16,
            color: c.neutral[5],
          },
          tickfont: {
            family: 'Proxima Nova, Arial',
            size: 12,
            color: c.neutral[5],
          },
        },
        yaxis: {
          fixedrange: true,
          range: [null, null],
          gridcolor: c.neutral[0],
          automargin: true,
          titlefont: {
            family: 'Proxima Nova, Arial',
            size: 16,
            color: c.neutral[5],
          },
          tickfont: {
            family: 'Proxima Nova, Arial',
            size: 12,
            color: c.neutral[5],
          },
        },
        yaxis2: {
          type: 'category',
          gridcolor: c.neutral[0],
          titlefont: {
            family: 'Proxima Nova, Arial',
            size: 16,
            color: c.neutral[5],
          },
          tickfont: {
            family: 'Proxima Nova, Arial',
            size: 12,
            color: c.neutral[5],
          },
          overlaying: 'y',
          side: 'right',
        },
        autosize: true,
        margin: this.margins,
        font: {
          family: 'Proxima Nova',
        },
        plot_bgcolor: 'transparent',
        paper_bgcolor: 'transparent',
        legend: {
          orientation: 'v',
          // x: 1,
          // y: 0.5,
          traceorder: 'reverse',
          font: {
            color: c.dark,
          },
        },
      };

      if (this.legendOnTop) {
        layout.legend.orientation = 'h';
        layout.legend.x = 0;
        layout.legend.y = 1.1;
        layout.margin.t = 10;
        layout.margin.r = 70;
      }

      if (this.alwaysShowLegend) {
        layout.showlegend = true;
      }

      if (noGaps) {
        layout.barmode = 'group';
        layout.bargap = 0;
        layout.bargroupgap = 0;
      }

      if (isPercent) {
        layout.yaxis = {
          ...layout.yaxis,
          tickformat: '.2p',
          hoverformat: '.2p',
          range: [yMin, 1],
        };
      }

      if (displayData.barmode) {
        layout.barmode = displayData.barmode;
      }

      if (displayData.xaxis) {
        layout.xaxis = {
          ...layout.xaxis,
          ...displayData.xaxis,
        };
      }

      if (binSize === '1M') {
        layout.xaxis = {
          ...layout.xaxis,
          tickvals: traces[0].x,
          ticktext: traces[0].x.map((x) => {
            const momentX = moment(x);
            const format = momentX.month() === 0 ? 'MMM YYYY' : 'MMM';
            return momentX.format(format);
          }),
        };
      }

      if (displayData.yaxis2) {
        layout.yaxis2 = {
          ...layout.yaxis2,
          ...displayData.yaxis2,
        };
      }

      if (displayData.yaxis) {
        const yAxis = displayData.yaxis;

        layout.yaxis = {
          ...layout.yaxis,
          ...displayData.yaxis,
        };

        // Axis scaling
        if (yAxis.start === 'auto') {
          layout.yaxis.range[0] = yMin;
        } else if (yAxis.start) {
          layout.yaxis.range[0] = yAxis.start;
        }
        if (yAxis.end === 'auto') {
          layout.yaxis.range[1] = traces.length === 1 ? getYMax(data) : null;
        } else if (yAxis.end) {
          layout.yaxis.range[1] = yAxis.end;

          if (!layout.yaxis.range[0]) {
            // We need to set this to 0 if there is no start specified,
            // otherwise the end value will not work
            layout.yaxis.range[0] = yMin;
          }
        }
      }

      if (displayData.type) {
        layout.xaxis.type = displayData.type;
      }

      return layout;
    },
    async run() {
      const { data, noGaps, noLegendTruncation } = this.chartParams;

      if (!data || data.length === 0
        || (data.length === 1 && data[0] && data[0].values && data[0].values.length === 0) // stack
      ) {
        this.showNoDataMessage = true;
        return;
      }

      this.showNoDataMessage = false;

      let traces = [];
      const couldShowLegend = data.filter((d) => !!d.key).length > 1;

      // Ensure the widget size has been updated before running
      await this.$nextTick();

      const chartWidth = this.$el.clientWidth;
      const maxLegendChars = Math.ceil(chartWidth / 30);

      data.forEach((d, traceIndex) => {
        const key = `${d.key}`;
        let name = key;
        if (!noLegendTruncation) {
          name = key.length > maxLegendChars ? `${key.substr(0, maxLegendChars - 3)}...` : key;
        }
        const traceObj = {
          name,
          type: d.type || 'bar',
          mode: d.mode || 'lines+markers',
          x: [],
          y: [],
          width: [],
          text: [],
          hoverinfo: d.hoverinfo || 'x+y+text',
          marker: {
            color: [],
          },
          line: {
            color: [],
            width: d.line && d.line.width !== undefined ? d.line.width : 2,
            shape: d.line && d.line.shape ? d.line.shape : 'linear',
          },
          fill: d.fill || 'none',
          fillcolor: d.fillcolor,
          showlegend: couldShowLegend && !!d.key,
        };

        if (traceObj.type === 'scatter') {
          traceObj.mode = 'markers';
        }

        if (d.yaxis) {
          traceObj.yaxis = d.yaxis;
        }

        if (!d.hoverinfo && data.length > 1) {
          traceObj.hoverinfo = 'x+y+text+name';
        }

        if (d.visible === false) {
          traceObj.visible = 'legendonly';
        }

        d.values.forEach((v) => {
          traceObj.x.push(v.x);
          traceObj.y.push(v.y);
          traceObj.text.push(v.text);

          if (v.width) {
            traceObj.width.push(v.width);
          }

          const opacity = v.opacity || 1;

          let colour;
          // let lineColour;
          const { chart } = this[PALETTE_COLOURS];
          const colourIndex = traceIndex % chart.length;
          if (traceObj.type === 'bar') {
            colour = withOpacity(chart[colourIndex], opacity);
          } else {
            colour = chart[colourIndex];
            // lineColour = withOpacity(chart[colourIndex], 0.2);
          }
          if (d.color) {
            colour = d.color;
          }
          if (v.colour) {
            ({ colour } = v);
          }

          if (traceObj.type === 'bar') {
            traceObj.marker.color.push(colour);
            traceObj.line.color.push(colour);

            // Disable text inside bars
            traceObj.textposition = 'none';
          } else {
            traceObj.marker.color = colour;
            traceObj.line.color = colour;
          }
        });

        traces.push(traceObj);
      });

      if (noGaps) {
        traces = traces.map((trace) => {
          const t = trace;
          t.connectgaps = true;
          return t;
        });
      }

      const layout = await this.getLayout(this.chartParams, traces);

      // Show the blocker before redrawing the chart
      this.readyToDisplay = false;
      await this.drawChart(traces, layout);
    },
    relayout() {
      this.test.relayout(this.$refs.element, {
        width: this.$refs.element.clientWidth,
        height: this.$refs.element.clientHeight,
      });
    },
    beforePrintHandle() {
      this.prevWidth = this.$refs.element.clientWidth;
      this.$refs.element.style.width = '210mm';
      this.relayout();
    },
    afterPrintHandle() {
      this.$refs.element.style.width = `${this.prevWidth}px`;
      this.relayout();
    },
    async drawChart(traces, layout) {
      const { Plotly, d3 } = await importPlotly();
      this.test = Plotly;
      d3Ref = d3;

      throttleDraw(Plotly, this.$refs.element, traces, layout).then(() => {
        const { element } = this.$refs;

        if (!element) return;

        window.addEventListener('beforeprint', this.beforePrintEvent);
        window.addEventListener('afterprint', this.afterPrintEvent);

        this.canResize = true;
        this.resize();

        const emitLegendClick = (trace) => {
          this.$emit('click-legend', trace);
        };

        if (this.allowLegendEvents) {
          // Remove any old legend events
          this.clearLegendEvents();

          // Attach legend events
          const legend = this.$refs.element.querySelector('g.legend');
          const vm = this;
          d3.select(legend).selectAll('.traces').each(function bindTrace() {
            const item = d3.select(this);
            item.on('click', (_, d) => {
              if (!d[0]) return;
              const { trace } = d[0];
              emitLegendClick(trace);
            });
            vm.legendElements.push(item);
          });
        }

        // Emit click events
        this.$refs.element.removeListener('plotly_click', this.handleClick);
        this.$refs.element.on('plotly_click', this.handleClick);
      });
    },
    handleClick(e) {
      this.$emit('click', e);

      if (Array.isArray(e.points) && e.points.length === 1) {
        this.$emit('select', e.points[0]);
      }
    },
    clearLegendEvents() {
      // d3Ref should be present if we are running this
      if (!d3Ref) return;

      this.legendElements.forEach((e) => {
        const item = d3Ref.select(e);
        item.on('click', null);
      });
      this.legendElements = [];
    },
  },
};
